import { formatCountdown, getDateStatus, parseDate } from '@shared/utils';
import { useFormatter, useTranslations } from 'next-intl';
import { useMemo } from 'react';
import { useCountdown } from '../components/OfferValidityBadge/hooks/useCountdown';

type DateStatus = { isExpired: boolean; isWithinNext24Hours: boolean };

/**
 * Hook for managing offer timer functionality
 * Provides countdown logic, date formatting, and button state management for offers with start dates
 */
const useOfferTimer = (offerValidFrom?: Date) => {
  const offerT = useTranslations('offers.page.obs.offer.cta.timer');
  const format = useFormatter();

  // Parse and validate the date once
  const parsedValidFromDate = useMemo(() => {
    return offerValidFrom ? parseDate(offerValidFrom) : null;
  }, [offerValidFrom]);

  // Set up countdown timer - determine if we should countdown
  const shouldCountdown = useMemo(() => {
    if (!parsedValidFromDate) return false;
    const status = getDateStatus(parsedValidFromDate);
    return status.isWithinNext24Hours && !status.isExpired;
  }, [parsedValidFromDate]);

  const currentTime = useCountdown(shouldCountdown);

  // Calculate date status
  const dateStatus = useMemo(() => {
    if (!parsedValidFromDate) return null;
    return getDateStatus(parsedValidFromDate, currentTime);
  }, [parsedValidFromDate, currentTime]);

  /**
   * Gets the appropriate button text based on offer start time
   */
  const getTimerButtonText = (originalText: string): string => {
    if (!parsedValidFromDate || !dateStatus) {
      return originalText;
    }

    // If the offer hasn't started yet (date is in the future)
    if (parsedValidFromDate > new Date()) {
      const { isWithinNext24Hours } = dateStatus;

      if (isWithinNext24Hours) {
        // Show countdown for offers starting within 24 hours
        const countdownText = formatCountdown(parsedValidFromDate, currentTime);
        return `${offerT('time')} ${countdownText}`;
      } else {
        // Show formatted date for offers starting in more than 24 hours
        const formattedDate = format.dateTime(parsedValidFromDate, { day: 'numeric', month: 'short', year: 'numeric' });
        return `${offerT('date')} ${formattedDate}`;
      }
    }

    // If the offer has already started or date is invalid, return original text
    return originalText;
  };

  /**
   * Determines if a button should be disabled due to offer timing
   */
  const isTimerDisabled = (): boolean => {
    // Check if offer hasn't started yet
    return Boolean(parsedValidFromDate && parsedValidFromDate > new Date());
  };

  return {
    parsedValidFromDate,
    dateStatus,
    getTimerButtonText,
    isTimerDisabled,
    shouldCountdown,
  };
};

export { useOfferTimer, type DateStatus };
